import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../config/Firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// Animation variants for the container
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

// Animation variants for the button
const buttonVariants = {
  hover: { scale: 1.05, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)' },
  tap: { scale: 0.95 },
};

const VisitorDetails = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [visitor, setVisitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showVisitHistory, setShowVisitHistory] = useState(false);
  const [formData, setFormData] = useState({
    regularCheckup: {
      leftEye: { spherical: '', cylindrical: '', axis: '', add: '' },
      rightEye: { spherical: '', cylindrical: '', axis: '', add: '' },
    },
    frameChange: { frameBrand: '', frameColor: '', framePrice: '' },
    lensChange: { lensBrand: '', lensPrice: '', coating: '' },
  });
  const [totalCost, setTotalCost] = useState(0);

  useEffect(() => {
    const fetchVisitor = async () => {
      setLoading(true);
      setError('');
      try {
        const docRef = doc(db, 'Visitors', email);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setVisitor({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Visitor not found.');
        }
      } catch (err) {
        setError('Failed to fetch visitor details. Please try again.');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVisitor();
  }, [email]);

  const handleInputChange = (category, field, value, eye = null) => {
    setFormData((prev) => {
      if (category === 'regularCheckup' && eye) {
        return {
          ...prev,
          regularCheckup: {
            ...prev.regularCheckup,
            [eye]: {
              ...prev.regularCheckup[eye],
              [field]: value,
            },
          },
        };
      }
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: value,
        },
      };
    });

    // Update total cost when framePrice or lensPrice changes
    if (field === 'framePrice' || field === 'lensPrice') {
      const framePrice = parseFloat(formData.frameChange.framePrice) || 0;
      const lensPrice = parseFloat(formData.lensChange.lensPrice) || 0;
      const newPrice = field === 'framePrice' ? parseFloat(value) || 0 : framePrice;
      const newLensPrice = field === 'lensPrice' ? parseFloat(value) || 0 : lensPrice;
      setTotalCost(newPrice + newLensPrice);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const docRef = doc(db, 'Visitors', email);
      const currentDate = new Date().toISOString();
      const visitData = {
        date: currentDate,
        regularCheckup: formData.regularCheckup,
        frameChange: formData.frameChange,
        lensChange: formData.lensChange,
        totalCost,
      };

      // Update the 'data' array in Firestore
      await updateDoc(docRef, {
        data: [...(visitor.data || []), visitData],
      });

      setShowUpdateForm(false);
      setFormData({
        regularCheckup: {
          leftEye: { spherical: '', cylindrical: '', axis: '', add: '' },
          rightEye: { spherical: '', cylindrical: '', axis: '', add: '' },
        },
        frameChange: { frameBrand: '', frameColor: '', framePrice: '' },
        lensChange: { lensBrand: '', lensPrice: '', coating: '' },
      });
      setTotalCost(0);
      // Refresh visitor data to include new visit
      const docRefRefresh = doc(db, 'Visitors', email);
      const docSnapRefresh = await getDoc(docRefRefresh);
      if (docSnapRefresh.exists()) {
        setVisitor({ id: docSnapRefresh.id, ...docSnapRefresh.data() });
      }
      alert('Visit details updated successfully!');
    } catch (err) {
      setError('Failed to update visit details. Please try again.');
      console.error('Error:', err);
    }
  };

  // Sort visit data by date (newest to oldest)
  const sortedVisitData = visitor?.data
    ? [...visitor.data].sort((a, b) => new Date(b.date) - new Date(a.date))
    : [];

  return (
    <motion.div
      className="w-full max-w-6xl mx-auto mt-8 px-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {loading && (
        <div className="text-center text-gray-600">Loading...</div>
      )}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4 border-2 border-black">
          {error}
        </div>
      )}
      {visitor && (
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Form */}
          {showUpdateForm && (
            <div className="lg:w-1/2 bg-gray-100 p-6 rounded-lg shadow-xl border-2 border-black">
              <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-black pb-2">
                Update Visit Details
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Regular Checkup */}
                <div className="bg-white p-4 rounded-md border-2 border-black">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Regular Checkup</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded border border-black">
                      <h5 className="font-medium text-gray-800 mb-2">Left Eye</h5>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Spherical"
                          value={formData.regularCheckup.leftEye.spherical}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'spherical', e.target.value, 'leftEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Cylindrical"
                          value={formData.regularCheckup.leftEye.cylindrical}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'cylindrical', e.target.value, 'leftEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Axis"
                          value={formData.regularCheckup.leftEye.axis}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'axis', e.target.value, 'leftEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Add"
                          value={formData.regularCheckup.leftEye.add}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'add', e.target.value, 'leftEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded border border-black">
                      <h5 className="font-medium text-gray-800 mb-2">Right Eye</h5>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Spherical"
                          value={formData.regularCheckup.rightEye.spherical}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'spherical', e.target.value, 'rightEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Cylindrical"
                          value={formData.regularCheckup.rightEye.cylindrical}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'cylindrical', e.target.value, 'rightEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Axis"
                          value={formData.regularCheckup.rightEye.axis}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'axis', e.target.value, 'rightEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Add"
                          value={formData.regularCheckup.rightEye.add}
                          onChange={(e) =>
                            handleInputChange('regularCheckup', 'add', e.target.value, 'rightEye')
                          }
                          className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Frame Change */}
                <div className="bg-white p-4 rounded-md border-2 border-black">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Frame Change</h4>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Frame Brand"
                      value={formData.frameChange.frameBrand}
                      onChange={(e) => handleInputChange('frameChange', 'frameBrand', e.target.value)}
                      className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Frame Color"
                      value={formData.frameChange.frameColor}
                      onChange={(e) => handleInputChange('frameChange', 'frameColor', e.target.value)}
                      className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Frame Price"
                      value={formData.frameChange.framePrice}
                      onChange={(e) => handleInputChange('frameChange', 'framePrice', e.target.value)}
                      className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                </div>
                
                {/* Lens Change */}
                <div className="bg-white p-4 rounded-md border-2 border-black">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Lens Change</h4>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Lens Brand"
                      value={formData.lensChange.lensBrand}
                      onChange={(e) => handleInputChange('lensChange', 'lensBrand', e.target.value)}
                      className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Lens Price"
                      value={formData.lensChange.lensPrice}
                      onChange={(e) => handleInputChange('lensChange', 'lensPrice', e.target.value)}
                      className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Coating"
                      value={formData.lensChange.coating}
                      onChange={(e) => handleInputChange('lensChange', 'coating', e.target.value)}
                      className="w-full p-2 border-2 border-black rounded-md focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                </div>
                
                {/* Total Cost */}
                <div className="bg-gray-900 text-white p-4 rounded-md border-2 border-black">
                  <h4 className="text-lg font-semibold mb-2">Total Cost</h4>
                  <p className="text-2xl font-bold">${totalCost.toFixed(2)}</p>
                </div>
                
                <motion.button
                  type="submit"
                  className="w-full bg-gray-900 text-white py-3 rounded-md font-semibold hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors duration-300 border-2 border-black"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Save Visit Details
                </motion.button>
              </form>
            </div>
          )}

          {/* Right Column - Visitor Details */}
          <div className={`${showUpdateForm ? 'lg:w-1/2' : 'w-full'} bg-white p-6 rounded-lg shadow-xl border-2 border-black`}>
            <div className="flex flex-col md:flex-row md:items-center gap-6 mb-6 border-b-2 border-black pb-4">
              {visitor.imageUrl && (
  <div className="flex-shrink-0">
    <img
      src={visitor.imageUrl}
      alt={visitor.fullName}
      className="w-32 h-32 object-cover rounded-full border-4 border-black shadow-lg"
    />
  </div>
)}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{visitor.fullName}</h2>
                <p className="text-gray-600">{visitor.email}</p>
                <p className="text-gray-600">{visitor.contact}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-md border border-black">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Gender</h3>
                <p className="text-gray-900">{visitor.gender || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-black">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Date of Birth</h3>
                <p className="text-gray-900">{visitor.dob || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-black">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Address</h3>
                <p className="text-gray-900">{visitor.address || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-black">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Created At</h3>
                <p className="text-gray-900">{visitor.createdAt || 'N/A'}</p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <motion.button
                className="flex-1 bg-gray-900 text-white py-2 rounded-md font-semibold hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors duration-300 border-2 border-black"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => navigate('/Visitor')}
              >
                Back to Search
              </motion.button>
              <motion.button
                className="flex-1 bg-blue-600 text-white py-2 rounded-md font-semibold hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 transition-colors duration-300 border-2 border-black"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => setShowUpdateForm(!showUpdateForm)}
              >
                {showUpdateForm ? 'Cancel' : 'Update Visit Details'}
              </motion.button>
              <motion.button
                className="flex-1 bg-purple-600 text-white py-2 rounded-md font-semibold hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 transition-colors duration-300 border-2 border-black"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => setShowVisitHistory(!showVisitHistory)}
              >
                {showVisitHistory ? 'Hide Visit History' : 'View Visit History'}
              </motion.button>
            </div>

            {/* Visit History */}
            {showVisitHistory && (
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-black pb-2">
                  Visit History
                </h3>
                {sortedVisitData.length === 0 ? (
                  <p className="text-gray-600 text-center py-4">No visit history available.</p>
                ) : (
                  <div className="space-y-4">
                    {sortedVisitData.map((visit, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-md border-2 border-black">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          Visit on {new Date(visit.date).toLocaleString()}
                        </h4>
                        
                        <div className="mb-3">
                          <h5 className="font-medium text-gray-800 mb-1">Regular Checkup</h5>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-3 rounded border border-black">
                              <h6 className="font-medium text-gray-700 mb-1">Left Eye</h6>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p>Spherical: {visit.regularCheckup.leftEye.spherical || 'N/A'}</p>
                                <p>Cylindrical: {visit.regularCheckup.leftEye.cylindrical || 'N/A'}</p>
                                <p>Axis: {visit.regularCheckup.leftEye.axis || 'N/A'}</p>
                                <p>Add: {visit.regularCheckup.leftEye.add || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded border border-black">
                              <h6 className="font-medium text-gray-700 mb-1">Right Eye</h6>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p>Spherical: {visit.regularCheckup.rightEye.spherical || 'N/A'}</p>
                                <p>Cylindrical: {visit.regularCheckup.rightEye.cylindrical || 'N/A'}</p>
                                <p>Axis: {visit.regularCheckup.rightEye.axis || 'N/A'}</p>
                                <p>Add: {visit.regularCheckup.rightEye.add || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="bg-white p-3 rounded border border-black">
                            <h5 className="font-medium text-gray-800 mb-1">Frame Change</h5>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Brand: {visit.frameChange.frameBrand || 'N/A'}</p>
                              <p>Color: {visit.frameChange.frameColor || 'N/A'}</p>
                              <p>Price: ${parseFloat(visit.frameChange.framePrice || 0).toFixed(2)}</p>
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded border border-black">
                            <h5 className="font-medium text-gray-800 mb-1">Lens Change</h5>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Brand: {visit.lensChange.lensBrand || 'N/A'}</p>
                              <p>Price: ${parseFloat(visit.lensChange.lensPrice || 0).toFixed(2)}</p>
                              <p>Coating: {visit.lensChange.coating || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gray-900 text-white p-3 rounded border-2 border-black">
                          <h5 className="font-medium mb-1">Total Cost</h5>
                          <p className="text-xl font-bold">${parseFloat(visit.totalCost || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default VisitorDetails;